import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  TextField,
  Paper,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { cyrillicToLatin } from 'src/helper/helperFunction';
import { useTranslation } from 'react-i18next';

const ReviewBySchoolPage = () => {
  const [statistics, setStatistics] = useState({});
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationSearchTerm, setLocationSearchTerm] = useState(''); // For Autocomplete search
  const [workStats, setWorkStats] = useState({});

  const navigate = useNavigate();
  const { t } = useTranslation();
  const fetchData = async () => {
    setLoading(true);
    try {
      const statisticsResponse = await axios.get(
        `${API_BASE_URL}/api/finalReviewAll.php?action=getInfoForReview`
      );
      setStatistics(statisticsResponse.data);

      const schoolsResponse = await axios.get(
        `${API_BASE_URL}/api/finalSchools.php?schoolsWithReviewCounts`
      );
      setSchools(schoolsResponse.data.schools || []);
      setFilteredSchools(schoolsResponse.data.schools || []);
    } catch (error) {
      console.error('Error fetching data', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/locations.php`);
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations', error);
    }
  };

  const fetchWorkStats = async () => {
    try {
      const [workResponse, reviewedResponse] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/api/finalConnectingWork.php?action=getNotConnectWork`
        ),
        axios.get(`${API_BASE_URL}/api/finalSchools.php?reviewedWorkCounts`)
      ]);

      const workData = workResponse.data;
      const reviewedData = reviewedResponse.data;

      const totalConnected = workData.reduce(
        (sum, item) => sum + Number(item.studentCountConnect || 0),
        0
      );
      const totalScanned = workData.reduce(
        (sum, item) => sum + Number(item.totalJpg || 0),
        0
      );
      const totalReviewed = Number(reviewedData.totalReviewed || 0);

      setWorkStats({
        totalConnected,
        totalScanned,
        totalReviewed
      });
    } catch (error) {
      console.error('Error fetching work stats', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchLocations();
    fetchWorkStats();
  }, []);

  const handleSchoolClick = (schoolId) => {
    navigate(`/final-class/${schoolId}`);
  };

  const handleLocationChange = (event, newValue) => {
    setSelectedLocation(newValue ? newValue.zipcode : '');
    filterSchools(newValue ? newValue.zipcode : '', searchTerm);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    filterSchools(selectedLocation ? selectedLocation.name : '', event.target.value);
  };

  const handleLocationSearchChange = (event, newValue) => {
    if (event && event.target) {
      setLocationSearchTerm(event.target.value);
    }
  };

  const filterSchools = (cityName, term) => {
    let filtered = schools;

    if (cityName) {
      filtered = filtered.filter(
        (school) => school.city && school.city === cityName
      );
    }

    if (term) {
      const normalizedTerm = cyrillicToLatin(term.toLowerCase());
      filtered = filtered.filter((school) => {
        const normalizedSchoolName = cyrillicToLatin(school.name.toLowerCase());
        return normalizedSchoolName.includes(normalizedTerm);
      });
    }

    setFilteredSchools(filtered);
  };
  const columns = [
    {
      field: 'name',
      headerName: t('review.class'),
      flex: 1,
      renderCell: (params) => (
        <Typography
          style={{
            textAlign: 'left',
            fontWeight: 'bold',
            color: '#1c75bc',
            cursor: 'pointer'
          }}
          onClick={() => handleSchoolClick(params.row.id)}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'student_count',
      headerName: t('review.student_count'),
      width: 120
    },
    {
      field: 'reviewed_students',
      headerName: t('review.reviewed_students'),
      width: 150
    },
    {
      field: 'scanned_works',
      headerName: t('review.scanned_works'),
      width: 150
    }
  ];

  return (
    <Paper
      elevation={4}
      sx={{
        padding: '20px',
        borderRadius: '16px',
        backgroundColor: '#f7f9fc',
        margin: '20px'
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ marginBottom: '20px' }}>
        {t('review.review_by_school_title')}
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={2}>
          {/* Leva strana - Tabela */}
          <Grid item xs={12} md={7}>
            <DataGrid
              rows={filteredSchools}
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              pagination
              sx={{
                '& .MuiDataGrid-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '10px',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #d1e3f3'
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                  padding: '12px',
                  fontSize: '14px',
                  color: '#333'
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#2596be',
                  color: '#ffffff',
                  borderBottom: 'none',
                  borderRadius: '12px 12px 0 0'
                },
                '& .MuiDataGrid-footerContainer': {
                  backgroundColor: '#2596be',
                  color: '#ffffff',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px'
                },
                '& .MuiCheckbox-root': {
                  color: '#28a745 !important'
                }
              }}
            />
          </Grid>

          {/* Desna strana - Pretraga, Filteri i Kartice */}
          <Grid item xs={12} md={5}>
            <Box>
              <TextField
                fullWidth
                label={t('review.search')}
                value={searchTerm}
                onChange={handleSearchChange}
                variant="outlined"
                sx={{ marginBottom: '20px' }}
              />

              <Grid container spacing={2}>
                {[
                  // KARTICE
                  {
                    title: t('review.total_students'),
                    value: statistics.total_students || 0,
                    icon: <PeopleIcon sx={{ fontSize: 40, color: '#007bff' }} />
                  },
                  {
                    title: t('review.total_connected_works'),
                    value: workStats.totalConnected || 0,
                    icon: <PeopleIcon sx={{ fontSize: 40, color: '#6f42c1' }} />
                  },
                  {
                    title: t('review.total_scanned_works'),
                    value: workStats.totalScanned || 0,
                    icon: <PeopleIcon sx={{ fontSize: 40, color: '#ff9800' }} />
                  },
                  {
                    title: t('review.total_reviewed_works'),
                    value: workStats.totalReviewed || 0,
                    icon: (
                      <CheckCircleIcon
                        sx={{ fontSize: 40, color: '#28a745' }}
                      />
                    )
                  }
                ].map((stat, index) => (
                  <Grid item xs={12} key={index}>
                    <Card
                      sx={{
                        backgroundColor: '#f0f4f7',
                        color: '#333',
                        padding: '20px',
                        borderRadius: '16px',
                        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
                        transition: 'transform 0.2s',
                        width: '100%',
                        height: '150px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          transform: 'translateY(-5px)'
                        }
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center">
                          {stat.icon}
                          <Box ml={2}>
                            <Typography variant="h6" fontWeight="bold">
                              {stat.title}
                            </Typography>
                            <Typography variant="h4">{stat.value}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default ReviewBySchoolPage;
